import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
	vehicles: [],
	workOrders: [],
	employees: [],
	inspections: [],
	dispensableItems: [],
	loading: false,
	error: null,
};

const FETCH_DASHBOARD_REQUEST = "FETCH_DASHBOARD_REQUEST";
const FETCH_DASHBOARD_SUCCESS = "FETCH_DASHBOARD_SUCCESS";
const FETCH_DASHBOARD_FAILURE = "FETCH_DASHBOARD_FAILURE";

// Helper to fetch inspection responses
const fetchInspectionResponses = async (inspectionId) => {
	try {
		const service = getService(serviceNames.perInspectionResponses);
		const response = await service.find({
			query: {
				inspectionId: inspectionId,
				isDeleted: false,
				$limit: -1,
			},
		});

		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching inspection responses:", error);
		return [];
	}
};

// Helper to fetch vehicle service requirements
const fetchVehicleServiceRequirements = async (vehicleId) => {
	try {
		const service = getService(serviceNames.perVehicleServiceRequirements);
		const response = await service.find({
			query: {
				vehicleId: vehicleId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const data = response?.data || response || [];
		return data.map((sr) => sr.serviceRequirementId);
	} catch (error) {
		console.error("Error fetching service requirements:", error);
		return [];
	}
};

export const getDashboardData = (userAgency) => async (dispatch) => {
	const vehiclesService = getService(serviceNames.perVehicles);
	const workOrdersService = getService(serviceNames.perWorkOrders);
	const employeesService = getService(serviceNames.personnel);
	const inspectionsService = getService(serviceNames.perInspections);
	const dispensableItemsService = getService(
		serviceNames.perDispensableItems,
	);

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: FETCH_DASHBOARD_REQUEST,
			success: FETCH_DASHBOARD_SUCCESS,
			failure: FETCH_DASHBOARD_FAILURE,
		},
		apiCall: async () => {
			// Fetch all data in parallel
			const [
				vehiclesResponse,
				workOrdersResponse,
				employeesResponse,
				inspectionsResponse,
				dispensableItemsResponse,
			] = await Promise.all([
				vehiclesService.find({
					query: {
						agencyId: userAgency,
						isDeleted: false,
						$limit: -1,
					},
				}),
				workOrdersService.find({
					query: {
						agencyId: userAgency,
						isDeleted: false,
						$limit: -1,
					},
				}),
				employeesService.find({
					query: {
						AgencyID: userAgency,
						IsDeleted: false,
						$limit: -1,
					},
				}),
				inspectionsService.find({
					query: {
						agencyId: userAgency,
						isDeleted: false,
						$limit: -1,
					},
				}),
				dispensableItemsService.find({
					query: {
						agencyId: userAgency,
						isDeleted: false,
						$limit: -1,
					},
				}),
			]);

			const vehiclesData =
				vehiclesResponse?.data || vehiclesResponse || [];
			const workOrdersData =
				workOrdersResponse?.data || workOrdersResponse || [];
			const employeesData =
				employeesResponse?.data || employeesResponse || [];
			const inspectionsData =
				inspectionsResponse?.data || inspectionsResponse || [];
			const dispensableItemsData =
				dispensableItemsResponse?.data ||
				dispensableItemsResponse ||
				[];

			// Fetch service requirements for each vehicle
			const vehiclesWithServiceReqs = await Promise.all(
				vehiclesData.map(async (vehicle) => {
					const serviceReqs = await fetchVehicleServiceRequirements(
						vehicle.id,
					);
					return {
						...vehicle,
						serviceRequirements: serviceReqs,
					};
				}),
			);

			// Fetch responses for each inspection
			const inspectionsWithResponses = await Promise.all(
				inspectionsData.map(async (inspection) => {
					const responses = await fetchInspectionResponses(
						inspection.id,
					);
					return {
						...inspection,
						responses: responses.map((r) => ({
							checklistItemId: r.checklistItemId,
							status: r.status,
							comment: r.comment,
						})),
					};
				}),
			);

			return {
				vehicles: vehiclesWithServiceReqs,
				workOrders: workOrdersData,
				employees: employeesData,
				inspections: inspectionsWithResponses,
				dispensableItems: dispensableItemsData,
			};
		},
		transformResult: (res) => res,
		errorMessagePrefix: "Failed to fetch dashboard data",
	});
};

export default function dashboardReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_DASHBOARD_REQUEST:
			return { ...state, loading: true, error: null };

		case FETCH_DASHBOARD_SUCCESS:
			return {
				...state,
				loading: false,
				vehicles: action.payload.vehicles,
				workOrders: action.payload.workOrders,
				employees: action.payload.employees,
				inspections: action.payload.inspections,
				dispensableItems: action.payload.dispensableItems,
			};

		case FETCH_DASHBOARD_FAILURE:
			return { ...state, loading: false, error: action.payload };

		default:
			return state;
	}
}
